import { ErrorMessage, useFormikContext } from "formik";
import React, { useContext, useEffect, useRef, useState } from "react";
import { BlobServiceClient } from "@azure/storage-blob";
import { saveAs } from "file-saver";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { useApiClient } from "../../../shared/customHooks";
import { Modal, ModalRef } from "../../modal/modal";
import { AdvisoryText } from "../../AdvisoryText";
import { FormContext } from "../context/formContext";
import { useMsal } from "@azure/msal-react";
import { postValidateFile } from "../scanner/ScannerService";
import { eventEmitter } from "../../../shared/event";
import { ToastInfo } from "../../toaster/toaster";
import { formatBits } from "../../../shared/CommonFunctions";
import { useGlobalContext } from "../../../shared/GlobalContext";
import ExtendedLabel from "../../extendedLabel/extendedLabel";

export interface FileUploadProps {
  uuid: string;
  name: string;
  label: string;
  container?: string;
  title: string;
  value: { files: fileData[] };
  warn?: string;
  disabled: boolean;
  externalForm?: boolean;
  multi?: boolean;
  extensions: string[];
  isManagerRole: boolean;
  isKindergartenRole: boolean;
  isRequired?: boolean;
  extendedlabel?: string;
  maxNumberOfFiles?: number;
  isPresub?: boolean;
  isAssessorRole?: boolean;
  isCurrent?: boolean;
  applicationBaseId?: number;
  isReferenceDataManagement?: boolean;
}

export default function FileUploadComponent({
  uuid,
  name,
  label,
  extendedlabel,
  container = "application-evidence",
  value,
  warn,
  disabled = false,
  multi = true,
  extensions,
  externalForm,
  isManagerRole,
  isKindergartenRole,
  isRequired,
  maxNumberOfFiles,
  isPresub,
  isAssessorRole,
  isReferenceDataManagement,
  isCurrent,
  applicationBaseId,
}: FileUploadProps) {
  const [invalidFile, setInvalidFile] = useState<string | null>();
  const { setFieldValue, isSubmitting, setFieldTouched } = useFormikContext();
  const { formContextStore } = useContext(FormContext);
  const { accounts } = useMsal();
  const { addLoadingUrl, removeLoadedUrl } = useGlobalContext();
  const { fetchData, putData } = useApiClient(applicationBaseId && isCurrent ? "fileHandler/" + applicationBaseId.toString() : "fileHandler", {
    requireAuth: formContextStore.requireAuth ?? true,
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const deleteFileModalRef = useRef<ModalRef>(null);
  const hideFileModalRef = useRef<ModalRef>(null);

  const fileExtensions = ["doc", "docx", "xls", "xlsx", "csv", "ppt", "pptx", "pdf", "jpeg", "jpg", "png", "tiff", "mp4"];

  const handleDragOver = (e: any) => {
    e.preventDefault();
  };

  const [filesToDelete, setFilesToDelete] = useState<fileData[]>([]);

  useEffect(() => {
    if (!isSubmitting) return;
    setInvalidFile(null);
    if (filesToDelete) {
      filesToDelete.forEach((file) => {
        putData(null, `hideFile?blobKey=${file.blobKey}&id=${file.id}`);
      });
    }
  }, [isSubmitting]);

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      if (maxNumberOfFiles && e.dataTransfer.files.length > maxNumberOfFiles) {
        tooManyFilesError();
      } else {
        handleFile(e);
        e.dataTransfer.clearData();
      }
    }
  };

  const handleChooseClick = () => {
    if (!maxNumberOfFiles || value?.files?.length < maxNumberOfFiles) {
      fileInputRef.current!.click();
    } else {
      tooManyFilesError();
    }
  };

  // DEV NOTE FOR AZURITE CORS! (curse who put cors on local storage emulation and not default to *)
  // Download/install https://azure.microsoft.com/en-us/products/storage/storage-explorer/
  // in EXPLORER side nav -> Emulator & Attached -> Storage Accounts -> (Emulator - Default Ports) (Key)
  // right click Blob Containers -> Configure CORS settings -> Add the values below
  // Allowed Origins: *, Allowed Headers: content-type,x-ms-*, Exposed Headers: Etag
  const handleFile = async (event: any) => {
    if (maxNumberOfFiles && value?.files?.length >= maxNumberOfFiles) {
      tooManyFilesError();
    } else {
      setInvalidFile(null);
      const newFiles = event.target.files ?? event.dataTransfer.files;
      if (newFiles.length == 0) return;
      const completeFiles = multi && value?.files != null ? [...value.files] : [];
      const account = accounts.length > 0 ? (accounts[0].idTokenClaims?.extension_UserEmail as string) : "";

      for (const file of multi ? [...newFiles] : [newFiles[0]]) {
        if (!(extensions ?? fileExtensions).includes(file.name.split(".")?.pop()!.toLowerCase())) {
          const message = extensions != null ? `Please upload a valid ${extensions.join(", ")} file` : "Please upload a valid type of file";
          setInvalidFile(message);
          setFieldTouched(name + ".files", false);
          return;
        }
        const fileName = `${uuid ? uuid + "/" : ""}${file.name}${new Date().getTime().toString()}`;
        if (file.size > 2.5e8) {
          setInvalidFile("File size must be less than 250mb");
          setFieldTouched(name + ".files", false);
          return;
        }

        try {
          const data = await fetchData("write", { filename: fileName, container: container });
          const { sas } = data.data;
          addLoadingUrl("processFileUploadSequence");
          const blobServiceClient = new BlobServiceClient(`${import.meta.env.VITE_STORAGEACCOUNT}${sas}`);
          const containerClient = blobServiceClient.getContainerClient(container);
          eventEmitter.emit("loading", { isLoading: true });
          await containerClient.getBlockBlobClient(fileName).upload(file, file.size, {
            onProgress: (ev) => {
              console.log(`${ev.loadedBytes} bytes transferred.`);
            },
            blobHTTPHeaders: {
              blobContentType: file.type,
            },
          });

          await postValidateFile(container + "/" + fileName).then((data) => {
            if (data?.data["res"] != "") {
              setInvalidFile("Invalid file upload. Please try again, if error persists contact support.");
              const toastData: ToastInfo = { title: "File Upload Error", message: data?.data["res"], severity: "error" };
              eventEmitter.emit("tstr", toastData);
              return;
            } else {
              const newFile: fileData = {
                fileName: file.name,
                size: file.size,
                file: file,
                contentType: file.type,
                emailCreated: account,
                lastModified: file.lastModified,
                blobKey: fileName,
                uploadDate: new Date().toISOString(),
                newFile: true,
              };
              completeFiles.push(newFile);
            }
          });
        } catch (err) {
          console.error(err);
        } finally {
          removeLoadedUrl("processFileUploadSequence");
          eventEmitter.emit("loading", { isLoading: false });
        }
      }
      setFieldTouched(name + ".files", false);
      await setFieldValue(name + ".files", completeFiles);
    }
  };

  const tooManyFilesError = () => {
    const errorData: ToastInfo = { title: "Error", message: "Only " + maxNumberOfFiles + " file/s may be uploaded", severity: "error" };
    eventEmitter.emit("tstr", errorData);
  };

  const downloadFile = (file: fileData) => {
    fetchData("read", { filename: file.blobKey, container: container }).then(({ data }) => {
      const { url } = data;
      const ext = file.contentType;
      const isProblematicExtension = ["rtf", "heic"].some((x) => ext.includes(x));
      if (isProblematicExtension) {
        fetch(url)
          .then((b) => b.blob())
          .then((blob) => {
            saveAs(blob, file.fileName);
          });
      } else {
        window.open(url, "_blank");
      }
    });
  };

  const deleteFile = () => {
    const f = [...value.files];
    f.splice(deleteFileIndx!, 1);
    setFieldValue(name, { files: f });
    if (fileInputRef.current != null) fileInputRef.current.value = "";
    deleteFileModalRef.current?.toggle();
  };

  const hideFile = () => {
    const f = [...value.files];
    setFilesToDelete([...filesToDelete, f[deleteFileIndx]]);
    hideFileModalRef.current?.toggle();
    setFieldValue(name, { files: f });
    setFieldTouched(name + ".files");
    f.splice(deleteFileIndx!, 1);
  };

  const [deleteFileIndx, setDeleteFileIndex] = useState<number>(0);

  return (
    <div className="py-2" id={name.split(".").pop()}>
      <div className="extentedlabel-container">
        <label className={isRequired ? "isRequired w-full" : ""}>{label}</label>
        {extendedlabel && <ExtendedLabel text={extendedlabel} />}
      </div>

      {warn && <AdvisoryText type="warning" label={warn} />}
      {!disabled && (
        <Card className="border shadow-none rounded-none!">
          <div onDragOver={handleDragOver} onDrop={handleDrop} className="flex flex-col items-center text-center p-2">
            Drag and drop or choose file to attach documents
            <Button type="button" label="Choose" icon="pi pi-upload" onClick={handleChooseClick} className="outline-button mt-3 choose-btn" />
          </div>
          <input type="file" multiple={multi} ref={fileInputRef} style={{ display: "none" }} onChange={handleFile} />
        </Card>
      )}
      {invalidFile && <div className="error">{invalidFile}</div>}
      <ErrorMessage name={name + ".files"} render={(msg) => <div className="error">{msg}</div>} />
      {value?.files?.length >= 1 && (
        <>
          <table className=" w-full mt-5 upload-table">
            <thead>
              <tr>
                <th className="p-2 w-3rem"></th>
                <th className="p-2">File name and type</th>
                <th className="p-2">File Size</th>
                <th className="p-2">Email of submitting user</th>
                <th className="p-2">Date and time stamp of the file upload</th>
                <th className="p-2 dont-show-in-print">Actions</th>
              </tr>
            </thead>
            <tbody>
              {value.files.map((rowData, index) => (
                <FileTemplate
                  key={index}
                  rowData={rowData}
                  index={index}
                  downloadFile={downloadFile}
                  setDeleteFileIndex={setDeleteFileIndex}
                  toggle={() => (rowData.dateCreated == null ? deleteFileModalRef.current?.toggle() : hideFileModalRef.current?.toggle())}
                  external={externalForm}
                  isManagerRole={isManagerRole}
                  isKindergartenRole={isKindergartenRole}
                  isPresub={isPresub}
                  isAssessorRole={isAssessorRole}
                  isReferenceDataManagement={isReferenceDataManagement}
                  disabled={disabled}
                />
              ))}
            </tbody>
          </table>
          <Modal
            ref={deleteFileModalRef}
            confirmAction={() => deleteFile()}
            headerLabel="Delete File"
            confirmLabel="Confirm"
            rejectLabel="Cancel"
            bodyText={`You are deleting '${value.files[deleteFileIndx]?.fileName}' file`}
          />

          <Modal
            ref={hideFileModalRef}
            confirmAction={() => hideFile()}
            headerLabel="Delete File"
            confirmLabel="Accept"
            rejectLabel="Cancel"
            bodyText={`Click on the Accept button to confirm that you'd like to delete this file. For files deleted in error, please contact IT Support.`}
          />
        </>
      )}
    </div>
  );
}

function FileTemplate({
  rowData,
  index,
  downloadFile,
  setDeleteFileIndex,
  toggle,
  isManagerRole,
  isKindergartenRole,
  isAssessorRole,
  isReferenceDataManagement,
  disabled,
}: {
  rowData: fileData;
  index: number;
  downloadFile: (rowData: fileData) => void;
  setDeleteFileIndex: (index: number) => void;
  toggle?: () => void;
  external?: boolean;
  isManagerRole: boolean;
  isKindergartenRole: boolean;
  isPresub?: boolean;
  isAssessorRole?: boolean;
  isReferenceDataManagement?: boolean;
  disabled?: boolean;
}) {
  const getFileIcon = (fileType: string): string => {
    switch (fileType) {
      case "application/pdf":
        return "pi pi-file-pdf";
      case "application/msword":
      case "application/vnd.openxmlformats-officedocument.wordprocessingml.document": // .docx
        return "pi pi-file-word";
      case "application/vnd.ms-excel":
      case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": // .xlsx
        return "pi pi-file-excel";
      case "image/jpeg":
      case "image/png":
      case "image/gif":
      case "image/bmp":
      case "image/webp":
        return "pi pi-image";
      default:
        return "pi pi-file";
    }
  };

  const [formattedData, setFormattedData] = useState<any>();

  useEffect(() => {
    const d = new Date(rowData.uploadDate);
    if (rowData.dateCreated) {
      d.setHours(d.getHours() - d.getTimezoneOffset() / 60);
    }
    const dta = {
      icon: getFileIcon(rowData.contentType),
      size: formatBits(rowData.size),
      uploadDate: `${d.toLocaleDateString("en-CA")} ${d.toLocaleString("en-CA", { timeStyle: "medium", hour12: false })}`,
    };
    setFormattedData(dta);
  }, [rowData]);

  return (
    <tr key={index} className="w-12 p-3 row">
      <td>
        <span className="ml-1 mt-1">{index + 1}</span>
      </td>

      <td className="column-width wrap-text flex items-baseline">
        <i className={formattedData?.icon} />
        <span className="ml-1 mt-1">{rowData.fileName}</span>
      </td>
      <td className="column-width">
        <span className="ml-1 mt-1">{formattedData?.size}</span>
      </td>

      <td className="column-width wrap-text">
        <span className="ml-1 mt-1">{rowData.emailCreated}</span>
      </td>
      <td className="column-width">
        <span className=" mt-1">{formattedData?.uploadDate}</span>
      </td>
      <td className=" flex mt-2 dont-show-in-print gap-2">
        {<Button label="Download" type="button" className="link pt-0" onClick={() => downloadFile(rowData)} icon="pi pi-download" />}
        {!disabled && (isManagerRole || isAssessorRole || isKindergartenRole || rowData.newFile) && !isReferenceDataManagement && (
          <Button
            label="Delete"
            type="button"
            className="link pt-0"
            onClick={() => {
              setDeleteFileIndex(index);
              toggle?.();
            }}
            icon="pi pi-trash"
          />
        )}
      </td>
    </tr>
  );
}

export interface fileData {
  lastModified: number;
  fileName: string;
  emailCreated?: string;
  size: number;
  file: File;
  contentType: string;
  blobKey: string;
  uploadDate: Date | string;
  dateCreated?: Date;
  newFile?: boolean;
  id?: number;
}
